import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import api from "../lib/api";
import { toast } from "sonner";
import { Trophy } from "lucide-react";
import ScoreInputCard from "./match-result/ScoreInputCard";
import MatchStatsTable from "./match-result/MatchStatsTable";

const emptyStat = (player_id) => ({
  player_id,
  goals: 0,
  assists: 0,
  yellow: 0,
  red: 0,
  minutes: 0,
});

export default function MatchResultDialog({
  open,
  onOpenChange,
  convocation,
  players,
  onSaved,
}) {
  const [scoreUs, setScoreUs] = useState(0);
  const [scoreThem, setScoreThem] = useState(0);
  const [stats, setStats] = useState({});

  useEffect(() => {
    if (!convocation) return;
    setScoreUs(convocation.final_score_us ?? 0);
    setScoreThem(convocation.final_score_them ?? 0);
    const map = {};
    (convocation.player_ids || []).forEach((pid) => {
      map[pid] = emptyStat(pid);
    });
    (convocation.match_stats || []).forEach((s) => {
      if (s.player_id) map[s.player_id] = { ...emptyStat(s.player_id), ...s };
    });
    setStats(map);
  }, [convocation]);

  const calledPlayers = useMemo(() => {
    if (!convocation) return [];
    return (convocation.player_ids || [])
      .map((pid) => players.find((p) => p.id === pid))
      .filter(Boolean)
      .sort((a, b) => (a.jersey_number || 99) - (b.jersey_number || 99));
  }, [convocation, players]);

  const totalGoals = useMemo(
    () => Object.values(stats).reduce((sum, s) => sum + (s?.goals || 0), 0),
    [stats]
  );

  if (!convocation) return null;

  const updateStat = (pid, field, value) => {
    setStats((s) => ({
      ...s,
      [pid]: { ...(s[pid] || emptyStat(pid)), [field]: Math.max(0, Number(value) || 0) },
    }));
  };

  const save = async () => {
    try {
      await api.post(`/convocations/${convocation.id}/result`, {
        final_score_us: Number(scoreUs),
        final_score_them: Number(scoreThem),
        match_stats: Object.values(stats),
      });
      toast.success("Risultato salvato");
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Risultato partita — {convocation.title}</DialogTitle>
          <DialogDescription className="sr-only">Inserisci il punteggio finale e le statistiche individuali dei giocatori.</DialogDescription>
        </DialogHeader>

        <ScoreInputCard
          scoreUs={scoreUs}
          scoreThem={scoreThem}
          opponentName={convocation.opponent}
          onChangeUs={setScoreUs}
          onChangeThem={setScoreThem}
          totalGoalsByPlayer={totalGoals}
        />

        <MatchStatsTable
          players={calledPlayers}
          stats={stats}
          onUpdate={updateStat}
          emptyStat={emptyStat}
        />

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button
            onClick={save}
            className="bg-sky-500 hover:bg-sky-600"
            data-testid="save-match-result-btn"
          >
            <Trophy size={14} className="mr-1" /> Salva risultato
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
